import { useMediaQuery } from '@mantine/hooks';
import { SHELL_MEDIA_QUERIES, type ShellBreakpoint } from '../lib/shellDensity';

export type { ShellBreakpoint } from '../lib/shellDensity';

/**
 * useShellBreakpoint — returns the current desktop-shell viewport tier.
 *
 * Shell: (desktop) only. The kiosk shell renders at a fixed viewport and should
 * not branch on this hook.
 *
 * - `compact`: viewport width &le; 1279px (13" laptops, shared screens).
 * - `default`: 1280–1600px (typical staff workstation; the design target).
 * - `wide`:    &ge; 1601px (large external monitors, staff-station kiosks).
 *
 * Non-obvious contracts:
 *   - The edges come from `lib/shellDensity.ts`, which is the single source for
 *     both the breakpoints and the row density they imply. They used to be bare
 *     strings here and again as `@media` blocks in `DesktopLayout.module.css`;
 *     see that module's header and issue #107.
 *   - Uses Mantine's `useMediaQuery`, which is `matchMedia`-based.
 *   - On SSR or test environments where `matchMedia` is missing, both queries
 *     resolve to `undefined`/`false` and the hook defaults to `'default'` —
 *     safe for snapshot tests and node renders.
 */
export function useShellBreakpoint(): ShellBreakpoint {
  const isCompact = useMediaQuery(SHELL_MEDIA_QUERIES.compact);
  const isWide = useMediaQuery(SHELL_MEDIA_QUERIES.wide);

  if (isCompact === true) return 'compact';
  if (isWide === true) return 'wide';
  return 'default';
}
